import type { MessageKey } from '@ralph/contracts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * What the runs domain's refusals say, in Brazilian Portuguese (checked against the English
 * shape at compile time).
 *
 * `execução` is feminine and `agente` is masculine (ADR-0018), which is why the participles
 * move between these entries: *esta execução já terminou*, *este agente foi descontinuado*.
 * `conversa`, never *bate-papo*; `execução`, never *rodada*.
 */
export const runs: Pick<Messages['errors'], Extract<MessageKey, `runs.${string}`>> = {
  // --- a run ---------------------------------------------------------------

  'runs.not_found': () => 'Essa execução não existe mais.',
  'runs.already_finished': () => 'Esta execução já terminou.',
  'runs.not_yours_to_cancel': () => 'Só quem iniciou esta execução pode cancelá-la.',
  'runs.checkpoint_unusable': () =>
    'Não dá para retomar esta execução: o que ela salvou está faltando ou foi gravado por uma versão anterior.',
  'runs.no_resolved_approval': () => 'Esta execução não tem nenhuma aprovação decidida para continuar.',
  'runs.delegation_parent_gone': ({ runId }) =>
    `A execução ${runId} não existe mais, então nada pode ser delegado a partir dela.`,

  // --- an approval ---------------------------------------------------------

  'runs.approval_not_found': () => 'Essa aprovação não existe mais.',
  'runs.approval_not_yours': () => 'Só quem iniciou esta execução pode decidir.',
  'runs.approval_already_resolved': () => 'Esta aprovação já foi decidida.',
  'runs.approval_expired': () => 'Esta aprovação expirou. Pergunte de novo para receber uma proposta nova.',
  'runs.approval_proposal_changed': () =>
    'A ação proposta mudou depois de ter sido mostrada a você. Revise a nova proposta antes de decidir.',
  'runs.edited_arguments_required': () => 'Para editar antes de aprovar, envie os argumentos editados.',
  'runs.edited_arguments_invalid': ({ tool }) =>
    `Os argumentos editados não são o que “${tool}” aceita. Confira e tente de novo.`,
  'runs.tool_gone': () => 'Essa ferramenta não está mais disponível.',

  // --- a conversation ------------------------------------------------------

  'runs.conversation_gone': () => 'Essa conversa não existe mais.',
  'runs.conversation_archived': () => 'Esta conversa está arquivada.',
  'runs.conversation_not_written': () => 'Não foi possível criar a conversa. Tente de novo.',
  'runs.message_pair_not_written': () => 'Não foi possível salvar sua mensagem. Tente de novo.',

  // --- the agent behind a conversation -------------------------------------

  // Indisponível e suspenso ficam em `agents.*`. A descontinuação fica aqui porque a frase
  // termina dizendo que a conversa continua legível, e só uma conversa pode dizer isso.
  'runs.agent_retired': () => 'Este agente foi descontinuado por quem o mantém. Você ainda pode ler esta conversa.',
  'runs.agent_retired_with_replacement': ({ replacement }) =>
    `Este agente foi descontinuado por quem o mantém — “${replacement}” entra no lugar dele. ` +
    'Você ainda pode ler esta conversa.',
  'runs.agent_drafted': () =>
    'Este agente não está ligado. Quem o mantém voltou com ele para rascunho, então não dá para enviar ' +
    'mensagens novas. Você ainda pode ler esta conversa.'
}
